import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  PAGES_PER_SPREAD,
  formatMinutes,
  journeySpreads,
  localizedFlightTypeLabel,
  type JourneyBookEntry,
  type JourneyBookFlight,
  type SpreadRow as SpreadRowCell,
} from '@jupi/shared'
import { ApiError, ConfirmDialog, RemoteContent, Title, formatDate, formatTime, useSnackbar } from '@jupi/ui'
import { useFlightLogAircraft, useJourneyBook, useJourneyEntryMutations } from '../../api/flightLog'
import { JourneyEntryDialog } from './JourneyEntryDialog'

// The electronic copy of the paper journey log book: one spread at a time,
// the rows in the paper's order and columns, with the brought-forward and
// carried-forward totals the paper writes at the head and foot of a spread.
// Placement and totals are computed on the server (journeyPlacement,
// journeyTotals); journeySpreads only lays them out. Reachable from the
// Fleet area; the selection lives in the URL. The engineer's entries and
// blank rows sit in the same rows as the flights, and can be added, corrected
// and deleted here by whoever the book view says may (canAddEntries).
export default function JourneyBookPage() {
  const { t, i18n } = useTranslation()
  const [params, setParams] = useSearchParams()
  const { data: aircraft = [] } = useFlightLogAircraft()
  const withLayout = aircraft.filter((a) => a.books.some((b) => b.layout))

  const registration = params.get('registration') ?? withLayout[0]?.registration ?? ''
  const selected = aircraft.find((a) => a.registration === registration)
  const books = selected?.books.filter((b) => b.layout) ?? []
  const bookNumber = Number(params.get('book')) || books.at(-1)?.bookNumber

  const { data, isLoading, error } = useJourneyBook(registration || undefined, bookNumber)
  const spreads = data ? journeySpreads(data.book, data.flights, data.entries) : []
  const { showSnackbar } = useSnackbar()
  const mutations = useJourneyEntryMutations(registration, bookNumber ?? 0)
  // The dialog: a new row, or the one being corrected. `dialogKey` starts it afresh.
  const [dialog, setDialog] = useState<{ entry?: JourneyBookEntry; key: number } | null>(null)
  const [deleting, setDeleting] = useState<JourneyBookEntry | null>(null)
  // The spread the next flight goes on, unless one is chosen.
  const chosen = Number(params.get('page')) || undefined
  const index = Math.max(0, chosen ? spreads.findIndex((s) => s.page === chosen) : spreads.length - 1)
  const spread = spreads[index]

  const select = (next: { registration?: string; book?: number; page?: number }) => {
    const p = new URLSearchParams()
    const reg = next.registration ?? registration
    if (reg) p.set('registration', reg)
    // A different aircraft has its own books: let the newest be chosen.
    const book = next.registration ? undefined : (next.book ?? bookNumber)
    if (book) p.set('book', String(book))
    if (next.page) p.set('page', String(next.page))
    setParams(p)
  }

  return (
    <Box>
      <Title label={t('flightLog.logbookTitle')} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t('flightLog.logbookIntro')}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select
          size="small"
          label={t('booking.aircraft')}
          value={registration}
          onChange={(e) => select({ registration: e.target.value })}
          sx={{ minWidth: 140 }}
        >
          {withLayout.map((a) => (
            <MenuItem key={a.registration} value={a.registration}>
              {a.registration}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('flightLog.journeyLogBook')}
          value={bookNumber ?? ''}
          onChange={(e) => select({ book: Number(e.target.value) })}
          sx={{ minWidth: 120 }}
        >
          {books.map((b) => (
            <MenuItem key={b.bookNumber} value={b.bookNumber}>
              {b.bookNumber}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {data?.canAddEntries && data.book.layout ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Icon icon="mdi:text-box-plus-outline" width={18} />}
          sx={{ mb: 2 }}
          onClick={() => setDialog({ key: Date.now() })}
        >
          {t('flightLog.entryAdd')}
        </Button>
      ) : null}
      {withLayout.length === 0 ? <Alert severity="info">{t('flightLog.noLogbook')}</Alert> : null}
      <RemoteContent isLoading={isLoading && Boolean(registration)} error={error}>
        {spread ? (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <IconButton
                size="small"
                aria-label={t('flightLog.previousSpread')}
                disabled={index === 0}
                onClick={() => select({ page: spreads[index - 1]!.page })}
              >
                <Icon icon="mdi:chevron-left" width={24} />
              </IconButton>
              <Typography variant="h6" component="h2">
                {t('flightLog.spreadPages', { from: spread.page, to: spread.page + PAGES_PER_SPREAD - 1 })}
              </Typography>
              <IconButton
                size="small"
                aria-label={t('flightLog.nextSpread')}
                disabled={index === spreads.length - 1}
                onClick={() => select({ page: spreads[index + 1]!.page })}
              >
                <Icon icon="mdi:chevron-right" width={24} />
              </IconButton>
              {spread.verified ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<Icon icon="mdi:check-decagram" width={16} />}
                  label={t('flightLog.verifiedFlight')}
                />
              ) : null}
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 1100, '& td, & th': { px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={2} />
                    <TableCell colSpan={9}>{t('flightLog.leftPage', { page: spread.page })}</TableCell>
                    <TableCell colSpan={4}>{t('flightLog.rightPage', { page: spread.page + 1 })}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('flightLog.colDate')}</TableCell>
                    <TableCell>{t('flightLog.colPilot')}</TableCell>
                    <TableCell>{t('flightLog.colCrew')}</TableCell>
                    <TableCell align="right">{t('flightLog.colPob')}</TableCell>
                    <TableCell>{t('flightLog.colFrom')}</TableCell>
                    <TableCell>{t('flightLog.colTo')}</TableCell>
                    <TableCell>{t('flightLog.colTakeoff')}</TableCell>
                    <TableCell>{t('flightLog.colLanding')}</TableCell>
                    <TableCell align="right">{t('flightLog.colAirborne')}</TableCell>
                    <TableCell align="right">{t('flightLog.colTotal')}</TableCell>
                    <TableCell align="right">{t('flightLog.colLandings')}</TableCell>
                    <TableCell>{t('flightLog.colNature')}</TableCell>
                    <TableCell>{t('flightLog.colFuel')}</TableCell>
                    <TableCell>{t('flightLog.colRemarks')}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={9}>{t('flightLog.broughtForward')}</TableCell>
                    <TableCell />
                    <TableCell align="right">{formatMinutes(spread.broughtForward.totalMinutes)}</TableCell>
                    <TableCell align="right">{spread.broughtForward.landings}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spread.rows.map((cell, i) => (
                    <SpreadRow
                      key={i}
                      row={i + 1}
                      cell={cell}
                      language={i18n.language}
                      onEdit={(entry) => setDialog({ entry, key: entry.entryId })}
                      onDelete={setDeleting}
                    />
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={9}>{t('flightLog.spreadTotal')}</TableCell>
                    <TableCell align="right">{formatMinutes(spread.flightMinutes)}</TableCell>
                    <TableCell />
                    <TableCell align="right">{spread.landings}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={9}>
                      <b>{t('flightLog.carriedForward')}</b>
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      <b>{formatMinutes(spread.carriedForward.totalMinutes)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{spread.carriedForward.landings}</b>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : data && !data.book.layout ? (
          <Alert severity="info">{t('flightLog.noLogbook')}</Alert>
        ) : null}
      </RemoteContent>
      {dialog && data && bookNumber ? (
        <JourneyEntryDialog
          key={dialog.key}
          open
          onClose={() => setDialog(null)}
          registration={registration}
          bookNumber={bookNumber}
          rowsPerSpread={data.book.layout?.rowsPerSpread ?? null}
          entry={dialog.entry}
          saving={mutations.create.isPending || mutations.update.isPending}
          onSave={(body) =>
            dialog.entry
              ? mutations.update.mutateAsync({ entryId: dialog.entry.entryId, body })
              : mutations.create.mutateAsync(body)
          }
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        title={t('flightLog.entryDelete')}
        message={t('flightLog.entryDeleteMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          const entry = deleting
          setDeleting(null)
          if (!entry) return
          try {
            await mutations.remove.mutateAsync(entry.entryId)
          } catch (err) {
            showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
          }
        }}
      />
    </Box>
  )
}

function SpreadRow({
  row,
  cell,
  language,
  onEdit,
  onDelete,
}: {
  row: number
  cell: SpreadRowCell<JourneyBookFlight, JourneyBookEntry>
  language: string
  onEdit: (entry: JourneyBookEntry) => void
  onDelete: (entry: JourneyBookEntry) => void
}) {
  const { t } = useTranslation()
  if (cell.kind === 'empty') {
    return (
      <TableRow sx={{ height: 36 }}>
        <TableCell sx={{ color: 'text.disabled' }}>{row}</TableCell>
        <TableCell colSpan={14} />
      </TableRow>
    )
  }
  if (cell.kind === 'blank') {
    // A row left blank on purpose, as the paper has it: shaded, not empty.
    return (
      <TableRow
        sx={{
          height: 36,
          bgcolor: 'action.hover',
          backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(128,128,128,0.12) 6px 7px)',
        }}
      >
        <TableCell sx={{ color: 'text.secondary' }}>{row}</TableCell>
        <TableCell colSpan={12} sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          {cell.part === 0 ? t('flightLog.entryBlankLabel', { count: cell.entry.rowCount }) : ''}
        </TableCell>
        <TableCell colSpan={2} align="right">
          {cell.part === 0 ? <EntryActions entry={cell.entry} onEdit={onEdit} onDelete={onDelete} /> : null}
        </TableCell>
      </TableRow>
    )
  }
  if (cell.kind === 'entry') {
    const { entry } = cell
    const first = cell.part === 0
    return (
      <TableRow sx={{ height: 36, bgcolor: 'action.selected', verticalAlign: 'top' }}>
        <TableCell sx={{ color: 'text.secondary' }}>{row}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{first ? formatDate(entry.occurredAt) : ''}</TableCell>
        <TableCell colSpan={11}>
          {first ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
              <Chip size="small" variant="outlined" icon={<Icon icon="mdi:wrench-outline" width={14} />} label={t('flightLog.entryChip')} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {entry.text}
                {entry.enteredByText ? ` — ${entry.enteredByText}` : ''}
              </Typography>
              {entry.maintenanceRecordId !== null ? (
                <Icon icon="mdi:link-variant" width={16} aria-label={t('flightLog.entryLinked')} />
              ) : null}
            </Stack>
          ) : null}
        </TableCell>
        <TableCell colSpan={2} align="right">
          {first ? <EntryActions entry={entry} onEdit={onEdit} onDelete={onDelete} /> : null}
        </TableCell>
      </TableRow>
    )
  }
  const flight = cell.flight
  const fuel = [flight.fuelAddedLitres != null ? `${flight.fuelAddedLitres} l` : null, flight.oilAddedLitres != null ? `${flight.oilAddedLitres} l oil` : null]
    .filter(Boolean)
    .join(' · ')
  // The paper writes the block times in the remarks column.
  const block = `${formatTime(flight.offBlockAt)}/${formatTime(flight.onBlockAt)}`
  return (
    <TableRow sx={{ height: 36 }}>
      <TableCell sx={{ color: 'text.secondary' }}>{row}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(flight.offBlockAt)}</TableCell>
      <TableCell>{flight.pilotName}</TableCell>
      <TableCell>{flight.crewName ?? ''}</TableCell>
      <TableCell align="right">{flight.personsOnBoard}</TableCell>
      <TableCell>{flight.departure}</TableCell>
      <TableCell>{flight.arrival}</TableCell>
      <TableCell>{formatTime(flight.takeoffAt)}</TableCell>
      <TableCell>{formatTime(flight.landingAt)}</TableCell>
      <TableCell align="right">{formatMinutes(flight.flightMinutes)}</TableCell>
      <TableCell align="right">{flight.totalMinutesAfter != null ? formatMinutes(flight.totalMinutesAfter) : ''}</TableCell>
      <TableCell align="right">{flight.landings}</TableCell>
      <TableCell>{localizedFlightTypeLabel(
          { label: flight.flightTypeLabel, labelSv: flight.flightTypeLabelSv, labelEn: flight.flightTypeLabelEn },
          language,
        )}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fuel}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {block}
        {flight.remarks ? ` · ${flight.remarks}` : ''}
      </TableCell>
    </TableRow>
  )
}

function EntryActions({
  entry,
  onEdit,
  onDelete,
}: {
  entry: JourneyBookEntry
  onEdit: (entry: JourneyBookEntry) => void
  onDelete: (entry: JourneyBookEntry) => void
}) {
  const { t } = useTranslation()
  if (!entry.canEdit) return null
  return (
    <>
      <IconButton size="small" aria-label={t('common.edit')} onClick={() => onEdit(entry)}>
        <Icon icon="mdi:pencil-outline" width={18} />
      </IconButton>
      <IconButton size="small" aria-label={t('common.delete')} onClick={() => onDelete(entry)}>
        <Icon icon="mdi:delete-outline" width={18} />
      </IconButton>
    </>
  )
}
